import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthContext } from '../../../../authContext';
import AlertsCustom from '../../../../components/Alerts';
import { translateBackendError } from '../../../../utils/translateBackendError';
import SkillNameConflictDialog from '../../../../components/SkillsManagerModal/SkillNameConflictDialog';
import SkillCard from '../../../../components/SkillsManagerModal/SkillCard';
import {
    SKILL_MAX_NAME_CHARS,
    SKILL_MAX_DESCRIPTION_CHARS,
    UnsupportedSkillFileError,
    getSkillFileSizes,
    extractSkillFileName,
    extractSkillFileDescription,
} from '../../../../components/SkillsManagerModal/skillUpload';
import '../../../../components/SkillsManagerModal/SkillsManagerModal.css';
import './SkillsTab.css';

function matchesSearch(skill, term) {
    const normalized = term.trim().toLowerCase();
    if (!normalized) return true;
    return (
        skill.name.toLowerCase().includes(normalized)
        || skill.description.toLowerCase().includes(normalized)
    );
}

// Tarefa 1.1 (specs/updated_self_assistant/plan.md): conteudo inline da aba
// "Skills" dedicada - substitui o botao "Gerenciar Skills" + SkillsManagerModal
// mode="link" que vivia dentro da aba Personalizacao. Layout/medidas/cores
// tiradas direto do arquivo Figma da spec (node 11863:19786 "Skill
// selecionada" / 11863:19949 "Sem nenhuma skill adicionada"): busca unica
// sobre as skills que o proprio usuario possui (aba "minhas" do backend -
// decisao do usuario: vincular so o que e' dele, sem as skills compartilhadas/
// TPF que apareciam no Gerenciador de Skills geral, e sem o botao de
// compartilhar que o modo de selecao antigo tinha) + lista separada "Skills
// adicionadas" (so aparece com pelo menos 1 skill vinculada, igual ao Figma)
// com "x" pra desvincular.
//
// "Adicione novas Skills" (decisao do usuario, revertendo o Open Item que
// antes deixava essa parte de fora): upload reaproveita a mesma validacao/
// POST /post_skill do Gerenciador de Skills geral (skillUpload.js), so' que
// a skill nova ja' sai vinculada a este assistente (POST /post_assistant_skill
// automatico). "Criar Skill" aciona `onCreateSkill` (resolvido pelo pai
// comum, AssistantConfiguration/index.js) - mesmo sinal `createSkillSignal`
// que o "Criar com Iris" do chat geral usa, consumido pelo UnifiedChat.js
// (useTestBox) que ja' roda no painel "Simulacao do Chat" ao lado. Isso so'
// passou a funcionar porque o backend deixou de bloquear "/skill-creator"
// dentro do self_assistant (self_assistant/controllers.py, decisao do
// usuario ao revisar esta tarefa - antes era um bloqueio deliberado, com
// teste dedicado garantindo "nunca").
function SkillsTab({ email, assistantId, onCreateSkill }) {
    const { t } = useTranslation('chat');
    const { t: tSelfAssist } = useTranslation('assistantConfiguration');
    const { accessToken } = useContext(AuthContext);

    const [availableSkills, setAvailableSkills] = useState([]);
    const [linkedSkills, setLinkedSkills] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [alert, setAlert] = useState(null);
    const [addingSkill, setAddingSkill] = useState(false);
    // Conflito de nome no upload - mesmo popup/estado do Gerenciador de
    // Skills geral (SkillsManagerModal), ver os comentarios de la: nomes de
    // skill sao unicos na plataforma, entao o backend responde 409 e o
    // usuario escolhe entre substituir (so' se a skill for dele) ou criar
    // uma nova com outro nome.
    const [nameConflict, setNameConflict] = useState(null);
    const [nameConflictError, setNameConflictError] = useState('');
    const addSkillInputRef = useRef(null);
    const toggleVersionsRef = useRef(new Map());

    const loadSkills = useCallback(() => {
        if (!assistantId) return undefined;

        let cancelled = false;
        setLoading(true);
        setLoadError(false);

        Promise.all([
            fetch(`${process.env.REACT_APP_API_URL}/get_skills?email=${encodeURIComponent(email)}&tab=minhas`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            }).then((response) => {
                if (!response.ok) throw new Error(`GET /get_skills falhou (${response.status})`);
                return response.json();
            }),
            fetch(`${process.env.REACT_APP_API_URL}/get_assistant_skills?email=${encodeURIComponent(email)}&assistant_id=${encodeURIComponent(assistantId)}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            }).then((response) => {
                if (!response.ok) throw new Error(`GET /get_assistant_skills falhou (${response.status})`);
                return response.json();
            }),
        ])
            .then(([allData, linkedData]) => {
                if (cancelled) return;
                setAvailableSkills(allData.skills || []);
                setLinkedSkills(linkedData.skills || []);
            })
            .catch(() => {
                if (!cancelled) setLoadError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [assistantId, email, accessToken]);

    useEffect(() => loadSkills(), [loadSkills]);

    const linkedIds = useMemo(() => new Set(linkedSkills.map((s) => s.skill_id)), [linkedSkills]);

    const filteredAvailable = availableSkills
        .filter((skill) => !linkedIds.has(skill.skill_id))
        .filter((skill) => matchesSearch(skill, searchTerm));

    // Vincula ou desvincula imediatamente. A lista local e a fonte visual de
    // verdade enquanto a requisicao esta em andamento, para permitir outra
    // acao sobre a mesma skill sem esperar a resposta anterior.
    const handleToggleLink = async (skill, wasLinked) => {
        const method = wasLinked ? 'DELETE' : 'POST';
        const url = wasLinked
            ? `${process.env.REACT_APP_API_URL}/delete_assistant_skill`
            : `${process.env.REACT_APP_API_URL}/post_assistant_skill`;
        const currentVersion = (toggleVersionsRef.current.get(skill.skill_id) || 0) + 1;
        toggleVersionsRef.current.set(skill.skill_id, currentVersion);

        setLinkedSkills((prev) => (
            wasLinked
                ? prev.filter((s) => s.skill_id !== skill.skill_id)
                : [...prev, skill]
        ));

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
                body: JSON.stringify({ email, assistant_id: assistantId, skill_id: skill.skill_id }),
            });
            if (!response.ok) throw new Error('toggle failed');
            await response.json().catch(() => ({}));
        } catch (error) {
            if (toggleVersionsRef.current.get(skill.skill_id) === currentVersion) {
                setLinkedSkills((prev) => (
                    wasLinked
                        ? [...prev, skill]
                        : prev.filter((s) => s.skill_id !== skill.skill_id)
                ));
                setAlert({
                    type: 'error',
                    title: t('skills_manager_link_error_title'),
                    message: t('skills_manager_link_error_message'),
                });
            }
        }
    };

    // POST /post_skill (mesma rota/validacao do Gerenciador de Skills geral)
    // + vinculo automatico a este assistente - o ponto de entrada foi a aba
    // Skills DESTE assistente, entao o upload ja nasce vinculado (sem passar
    // pelo "+" da grade de selecao). Extraido a parte (como no modal geral)
    // para ser chamado tanto direto (nome inedito) quanto apos confirmar a
    // substituicao de uma skill propria ja existente.
    const submitSkillFile = async (file, { novoNome = null, substituir = false } = {}) => {
        const formData = new FormData();
        formData.append('email', email);
        formData.append('file', file);
        if (novoNome) formData.append('new_name', novoNome);
        if (substituir) formData.append('substituir', 'true');

        const response = await fetch(`${process.env.REACT_APP_API_URL}/post_skill`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}` },
            body: formData,
        });
        const data = await response.json().catch(() => ({}));

        if (response.status === 409) {
            return { conflict: data.name_conflict || { name: novoNome, owned_by_user: false } };
        }

        if (!response.ok) {
            setAlert({
                type: 'error',
                title: t('skills_manager_add_error_title'),
                message: translateBackendError(data.error) || t('skills_manager_add_error_title'),
            });
            return { erro: true };
        }

        setAvailableSkills((prev) => [data, ...prev.filter((s) => s.skill_id !== data.skill_id)]);

        try {
            const linkResponse = await fetch(`${process.env.REACT_APP_API_URL}/post_assistant_skill`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
                body: JSON.stringify({ email, assistant_id: assistantId, skill_id: data.skill_id }),
            });
            if (linkResponse.ok) {
                const linkData = await linkResponse.json();
                setLinkedSkills(linkData.skills || []);
            }
        } catch (error) {
            // A skill foi criada mas o vinculo automatico falhou - ela segue
            // visivel na grade de selecao (o "+" continua disponivel pra
            // vincular manualmente), sem bloquear o upload em si por isso.
        }

        return { ok: true };
    };

    // Mesma validacao (extensao/nome/descricao) do "Adicionar Skill" do
    // Gerenciador de Skills geral, antes de qualquer chamada de rede.
    const handleAddSkillFileChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        setAddingSkill(true);
        try {
            await getSkillFileSizes(file);

            const skillName = await extractSkillFileName(file);
            if (skillName && skillName.length > SKILL_MAX_NAME_CHARS) {
                setAlert({
                    type: 'error',
                    title: t('skills_manager_name_limit_exceeded_title'),
                    message: t('skills_manager_name_limit_exceeded_message', {
                        chars: skillName.length,
                        limit: SKILL_MAX_NAME_CHARS,
                    }),
                });
                return;
            }

            const skillDescription = await extractSkillFileDescription(file);
            if (skillDescription && skillDescription.length > SKILL_MAX_DESCRIPTION_CHARS) {
                setAlert({
                    type: 'error',
                    title: t('skills_manager_description_limit_exceeded_title'),
                    message: t('skills_manager_description_limit_exceeded_message', {
                        chars: skillDescription.length,
                        limit: SKILL_MAX_DESCRIPTION_CHARS,
                    }),
                });
                return;
            }

            // Nome ja usado por uma skill PROPRIA existente - abre o popup
            // sem gastar a ida ao backend (mesmo padrao do modal geral). A
            // grade daqui lista tambem skills de outras pessoas, entao o
            // dono e' checado: substituir so' faz sentido na propria.
            if (skillName) {
                const existing = availableSkills.find(
                    (s) => s.name === skillName && s.owner_email === email
                );
                if (existing) {
                    setNameConflictError('');
                    setNameConflict({
                        file, name: existing.name, owned_by_user: true, skill_id: existing.skill_id,
                    });
                    return;
                }
            }

            const resultado = await submitSkillFile(file);
            if (resultado?.conflict) {
                setNameConflictError('');
                setNameConflict({ file, ...resultado.conflict });
            }
        } catch (error) {
            const message = error instanceof UnsupportedSkillFileError
                ? t('skills_manager_add_unsupported_format')
                : t('skills_manager_add_error_title');
            setAlert({ type: 'error', title: t('skills_manager_add_error_title'), message });
        } finally {
            setAddingSkill(false);
        }
    };

    const handleCancelNameConflict = () => {
        setNameConflict(null);
        setNameConflictError('');
    };

    // Ver o gemeo em components/SkillsManagerModal/index.js: reenvia o mesmo
    // arquivo mudando so' a instrucao, e um novo 409 mantem o popup aberto.
    const reenviarSkillEmConflito = async (opcoes, nomeTentado) => {
        const pendente = nameConflict;
        if (!pendente) return;

        setAddingSkill(true);
        try {
            const resultado = await submitSkillFile(pendente.file, opcoes);
            if (resultado?.conflict) {
                setNameConflict({ file: pendente.file, ...resultado.conflict });
                setNameConflictError(t('skills_manager_name_conflict_taken', {
                    name: nomeTentado || pendente.name,
                }));
                return;
            }
            handleCancelNameConflict();
        } catch (error) {
            handleCancelNameConflict();
            setAlert({
                type: 'error',
                title: t('skills_manager_add_error_title'),
                message: t('skills_manager_add_error_title'),
            });
        } finally {
            setAddingSkill(false);
        }
    };

    const handleReplaceConflictingSkill = () => reenviarSkillEmConflito({ substituir: true });

    const handleCreateWithNewName = (novoNome) => reenviarSkillEmConflito({ novoNome }, novoNome);

    if (!assistantId) {
        return <p className="skills-capability-hint">{tSelfAssist('skills_config_save_first')}</p>;
    }

    return (
        <div className="assistant-skills-tab" data-testid="assistant-skills-tab">
            <div className="assistant-skills-add" data-testid="assistant-skills-add">
                <p className="assistant-skills-column-title">{tSelfAssist('skills_tab_add_title')}</p>
                <div className="assistant-skills-add-row">
                    <label
                        className={`assistant-skills-dropzone${addingSkill ? ' assistant-skills-dropzone--loading' : ''}`}
                        data-testid="assistant-skills-upload-dropzone"
                    >
                        {/* Retorno visual imediato (decisao do usuario): sem isso, o
                            dropzone ficava com a mesma aparencia entre o usuario
                            escolher o arquivo e o upload terminar - so' `disabled`
                            no input, sem nenhuma indicacao visivel de que algo
                            estava acontecendo. */}
                        {addingSkill ? (
                            <>
                                <CircularProgress size={32} thickness={4} data-testid="assistant-skills-upload-spinner" />
                                <span className="assistant-skills-dropzone-text">
                                    {tSelfAssist('skills_tab_uploading_text')}
                                </span>
                            </>
                        ) : (
                            <>
                                <img src="/imagens/skills-upload-cloud-icon.svg" alt="" className="assistant-skills-dropzone-icon" />
                                <span className="assistant-skills-dropzone-text">
                                    {tSelfAssist('skills_tab_upload_text')}
                                    <br />
                                    <span className="assistant-skills-dropzone-link">{tSelfAssist('skills_tab_upload_link')}</span>
                                </span>
                            </>
                        )}
                        <input
                            type="file"
                            accept=".md,.zip,.skill"
                            ref={addSkillInputRef}
                            onChange={handleAddSkillFileChange}
                            disabled={addingSkill}
                            style={{ display: 'none' }}
                            data-testid="assistant-skills-upload-input"
                        />
                    </label>

                    <button
                        type="button"
                        className="assistant-skills-create-card"
                        data-testid="assistant-skills-create-button"
                        onClick={onCreateSkill}
                    >
                        <img src="/imagens/skills-create-with-iris-icon.svg" alt="" className="assistant-skills-create-icon" />
                        <p className="assistant-skills-create-title">{tSelfAssist('skills_tab_create_title')}</p>
                    </button>
                </div>
            </div>

            <div className={`assistant-skills-columns${linkedSkills.length > 0 ? ' assistant-skills-columns--has-linked' : ''}`}>
                <div className="assistant-skills-select" data-testid="assistant-skills-select">
                    <p className="assistant-skills-column-title">{tSelfAssist('skills_tab_select_title')}</p>
                    <p className="assistant-skills-column-subtitle">{tSelfAssist('skills_tab_select_subtitle')}</p>

                    <div className="skills-manager-search">
                        <img src="/imagens/search.svg" alt="" className="skills-manager-search-icon" />
                        <input
                            type="text"
                            className="skills-manager-search-input"
                            placeholder={t('skills_manager_search_placeholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            data-testid="assistant-skills-search-input"
                        />
                    </div>

                    <div className="assistant-skills-grid" data-testid="assistant-skills-grid">
                        {loading ? (
                            <div className="skills-manager-loading" data-testid="assistant-skills-loading">
                                <CircularProgress size={32} thickness={4} />
                            </div>
                        ) : loadError ? (
                            <div className="skills-manager-error" data-testid="assistant-skills-error">
                                <p className="skills-manager-error-message">{t('skills_manager_load_error_message')}</p>
                                <button
                                    type="button"
                                    className="skills-manager-error-retry"
                                    data-testid="assistant-skills-retry"
                                    onClick={loadSkills}
                                >
                                    {t('skills_manager_load_error_retry')}
                                </button>
                            </div>
                        ) : (
                            filteredAvailable.map((skill) => (
                                <SkillCard
                                    key={skill.skill_id}
                                    skill={skill}
                                    selectable
                                    onToggleSelect={() => handleToggleLink(skill, false)}
                                />
                            ))
                        )}
                    </div>
                </div>

                {/* Coluna "Skills adicionadas" - so aparece com pelo menos 1 skill
                    vinculada, igual ao design do Figma (estado vazio nao reserva
                    espaco, a grade de selecao usa a largura toda). */}
                {linkedSkills.length > 0 && (
                    <div className="assistant-skills-linked" data-testid="assistant-skills-linked">
                        <p className="assistant-skills-column-title">{tSelfAssist('skills_tab_linked_title')}</p>
                        <p className="assistant-skills-column-subtitle">{tSelfAssist('skills_tab_linked_subtitle')}</p>

                        <div className="assistant-skills-chip-list" data-testid="assistant-skills-chip-list">
                            {linkedSkills.map((skill) => (
                                <div className="assistant-skills-chip" key={skill.skill_id} data-testid={`assistant-skills-chip-${skill.skill_id}`}>
                                    <span className="assistant-skills-chip-name" title={`/${skill.name}`}>/{skill.name}</span>
                                    <button
                                        type="button"
                                        className="assistant-skills-chip-remove"
                                        data-testid="assistant-skills-chip-remove"
                                        onClick={() => handleToggleLink(skill, true)}
                                    >
                                        <img src="/imagens/x.svg" alt={tSelfAssist('skills_tab_remove_alt')} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {alert && (
                <AlertsCustom
                    type={alert.type}
                    title={alert.title}
                    message={alert.message}
                    onClose={() => setAlert(null)}
                />
            )}

            <SkillNameConflictDialog
                isOpen={nameConflict !== null}
                conflict={nameConflict}
                maxNameChars={SKILL_MAX_NAME_CHARS}
                busy={addingSkill}
                errorMessage={nameConflictError}
                onReplace={handleReplaceConflictingSkill}
                onCreateWithName={handleCreateWithNewName}
                onCancel={handleCancelNameConflict}
            />
        </div>
    );
}

export default SkillsTab;
